import { Types } from '../../duck';
import InputLayout from '../input-layout';
import {
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputGroupProps,
  InputLeftElement,
  InputProps,
  InputRightElement,
  Textarea,
  TextareaProps,
} from '@chakra-ui/react';
import { AiOutlineEye } from '@react-icons/all-files/ai/AiOutlineEye';
import { AiOutlineEyeInvisible } from '@react-icons/all-files/ai/AiOutlineEyeInvisible';
import React from 'react';
import { IoSearchOutline } from 'react-icons/io5';
import { useField } from '@lon/mathnation-suit/hooks';
import { generateId, mergeRefs } from '@lon/mathnation-suit/utils';
import { TextInputInstance } from './duck';

export interface TextInputProps
  extends Omit<InputProps & TextareaProps, 'name' | 'onChange'>,
    Types.BaseInputProps {
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
  type?: 'text' | 'email' | 'password' | 'tel' | 'text-area';
  inputGroupProps?: InputGroupProps;
  inputLeftElement?: React.ReactElement;
  inputRightElement?: React.ReactElement;
  hasSearchIcon?: boolean;
  onKeyDown?: React.KeyboardEventHandler;
  instanceRef?: React.MutableRefObject<TextInputInstance | null>;
  innerRef?: React.MutableRefObject<HTMLInputElement | null>;
  isRequired?: boolean;
}

const InnerTextInput: React.FC<TextInputProps> = ({
  label,
  name,
  labelHidden = false,
  type = 'text',
  inputLayoutProps,
  inputLeftElement,
  inputRightElement,
  inputGroupProps,
  onChange,
  onKeyDown,
  value,
  defaultValue,
  hasSearchIcon = false,
  instanceRef,
  innerRef,
  isRequired,
  isDisabled,
  size,
  ...rest
}) => {
  const labelId = React.useMemo(() => name || generateId(), [name]);
  const fieldProps = useField(name);
  const valueToUse =
    value !== undefined ? value || '' : fieldProps.value || undefined;

  const commonInputProps = {
    ref: mergeRefs(fieldProps.ref, innerRef),
    type,
    background: 'white',
    name: labelId,
    defaultValue,
    value: valueToUse,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      } else if (fieldProps.onChange) {
        fieldProps.onChange(e);
      }
    },
    onBlur: fieldProps.onBlur,
    isDisabled,
    ...rest,
  };

  /* Password Input */
  const [show, setShow] = React.useState(false);
  React.useImperativeHandle(instanceRef, () => ({
    togglePassword: (newState: boolean) => setShow(newState),
  }));

  return (
    <InputLayout
      isRequired={isRequired}
      id={labelId}
      label={label}
      labelHidden={labelHidden}
      errorMessage={fieldProps.error}
      input={
        <InputGroup size={size} {...inputGroupProps}>
          {inputLeftElement && (
            <InputLeftElement
              zIndex={1}
              pointerEvents={isDisabled ? 'none' : undefined}
            >
              {inputLeftElement}
            </InputLeftElement>
          )}

          {type === 'text-area' ? (
            <Textarea {...(commonInputProps as any)} />
          ) : (
            <Input
              {...commonInputProps}
              type={type === 'password' ? (show ? 'text' : 'password') : type}
            />
          )}

          {inputRightElement && (
            <InputRightElement
              zIndex={1}
              pointerEvents={isDisabled ? 'none' : undefined}
              opacity={isDisabled ? 0.4 : 1}
            >
              {inputRightElement}
            </InputRightElement>
          )}

          {hasSearchIcon && (
            <InputRightElement pointerEvents="none" zIndex={1}>
              <Icon as={IoSearchOutline} />
            </InputRightElement>
          )}

          {type === 'password' && (
            <InputRightElement zIndex={1}>
              <IconButton
                minHeight="30px"
                minWidth="30px"
                height="30px"
                variant="ghost"
                borderRadius="full"
                colorScheme="blackAlpha"
                onClick={() => setShow((prev) => !prev)}
                aria-label="Show/Hide password"
                icon={
                  <Icon
                    boxSize="6"
                    as={show ? AiOutlineEyeInvisible : AiOutlineEye}
                  />
                }
              />
            </InputRightElement>
          )}
        </InputGroup>
      }
      {...inputLayoutProps}
    />
  );
};

export default InnerTextInput;
